import React from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { Breadcrumbs, Link, Typography } from '@mui/material';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { locateRoute, visibleLinksFor } from './moduleCatalog';
import { useModuleVisibility } from './useModuleVisibility';

/**
 * "All Modules › Attendance › Employee Attendance" above every module page.
 *
 * Gives the user a way back up out of a leaf page without reaching for the browser's back button.
 * The trail is derived from the shared catalog, so a page added there is labelled here for free;
 * routes the catalog does not know (a profile page, say) render nothing.
 */
/**
 * Every rule here is breakpoint-keyed, deliberately: the desktop breadcrumb must render exactly as
 * it did before this branch. `display: flex` only pairs with the nowrap/ellipsis truncation below
 * sm, so it is scoped too rather than being the file's one unscoped desktop change.
 */
export const breadcrumbsSx = {
    display: { xs: 'flex', md: undefined },
    fontSize: { xs: '0.75rem', sm: '0.875rem' },
    mb: { xs: 1, sm: 2 },
    '@media (max-width:599.95px)': {
        flexWrap: 'nowrap',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        '& .MuiBreadcrumbs-ol': { flexWrap: 'nowrap' },
        '& .MuiBreadcrumbs-li': { minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' },
    },
};

const ModuleBreadcrumbs = () => {
    const { pathname } = useLocation();
    const { visibility } = useModuleVisibility();

    // The launcher pages carry their own back link, and the dashboard is the root.
    if (pathname === '/' || pathname === '/modules' || pathname.startsWith('/modules/')) return null;

    const here = locateRoute(pathname);
    if (!here) return null;

    const { card, link, exact } = here;
    // A module with several reachable pages goes back to its card grid; one without goes to the
    // page it landed on, which is that module's own list.
    const moduleHref = visibleLinksFor(card, visibility).length > 1 ? `/modules/${card.key}` : card.path;

    return (
        <Breadcrumbs
            separator={<NavigateNextIcon fontSize="small" />}
            aria-label="breadcrumb"
            sx={breadcrumbsSx}
        >
            <Link component={RouterLink} to="/modules" underline="hover" color="inherit">
                All Modules
            </Link>

            {link ? (
                <Link component={RouterLink} to={moduleHref} underline="hover" color="inherit">
                    {card.label}
                </Link>
            ) : exact ? (
                <Typography color="text.primary" fontSize="inherit">{card.label}</Typography>
            ) : (
                <Link component={RouterLink} to={card.path} underline="hover" color="inherit">
                    {card.label}
                </Link>
            )}

            {link && (exact ? (
                <Typography color="text.primary" fontSize="inherit">{link.label}</Typography>
            ) : (
                <Link component={RouterLink} to={link.path} underline="hover" color="inherit">
                    {link.label}
                </Link>
            ))}
        </Breadcrumbs>
    );
};

export default ModuleBreadcrumbs;
